import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { formatDateTime } from '../../utils/timezoneUtils';

interface TicketReply {
  id: number;
  sender: 'client' | 'admin';
  message: string;
  attachment: {
    path: string;
    filename: string;
  } | null;
  created_at: string;
}

interface TicketDetail {
  id: number;
  client_name: string;
  client_email: string;
  request: {
    id: number;
    request_number: string;
    company_name: string;
    package_name: string;
    country: string;
  };
  status: 'waiting_for_client' | 'answered' | 'processing' | 'closed';
  created_at: string;
  updated_at: string;
  replies: TicketReply[];
}

const AdminTicketDetail: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetail();
    }
  }, [ticketId]);

  const fetchTicketDetail = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`https://patrixglobal.com/backend/api/admin/tickets/get.php?id=${ticketId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ticket details');
      }

      const data = await response.json();
      if (data.success && data.ticket) {
        // Transform the backend response to match our frontend interface
        const transformedTicket = {
          id: data.ticket.id,
          client_name: data.ticket.client.name,
          client_email: data.ticket.client.email,
          request: {
            id: data.ticket.request.id,
            request_number: data.ticket.request.number,
            company_name: data.ticket.request.company_name,
            package_name: data.ticket.request.package_name,
            country: data.ticket.request.country_name
          },
          status: data.ticket.status,
          created_at: data.ticket.created_at,
          updated_at: data.ticket.updated_at,
          replies: data.replies
        };
        setTicket(transformedTicket);
      } else {
        throw new Error(data.error || 'Failed to load ticket details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyMessage.trim() || !ticketId) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      let body;
      let headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
        'X-Requested-With': 'XMLHttpRequest', // CSRF protection
      };

      if (replyFile) {
        const formData = new FormData();
        formData.append('ticket_id', ticketId);
        formData.append('message', replyMessage);
        formData.append('attachment', replyFile);
        body = formData;
        // Don't set Content-Type for FormData - browser will set it automatically with boundary
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          ticket_id: parseInt(ticketId),
          message: replyMessage
        });
      }

      const response = await fetch('https://patrixglobal.com/backend/api/admin/tickets/get.php', {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error('Failed to send reply');
      }

      const data = await response.json();
      if (data.message) {
        setReplyMessage('');
        setReplyFile(null);
        fetchTicketDetail(); // Refresh ticket details
      } else {
        throw new Error(data.error || 'Failed to send reply');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!ticketId) return;

    setStatusUpdating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('https://patrixglobal.com/backend/api/admin/tickets/update_status.php', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket_id: parseInt(ticketId),
          status: newStatus
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      const data = await response.json();
      if (data.message) {
        fetchTicketDetail(); // Refresh ticket details
      } else {
        throw new Error(data.error || 'Failed to update status');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'waiting_for_client': { text: 'Waiting for Client', color: 'bg-yellow-100 text-yellow-800' },
      'answered': { text: 'Answered', color: 'bg-blue-100 text-blue-800' },
      'processing': { text: 'Processing', color: 'bg-green-100 text-green-800' },
      'closed': { text: 'Closed', color: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.processing;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const formatDateDisplay = (dateString: string) => {
    return formatDateTime(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }, 'en-US');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      
      if (file.size > maxSize) {
        alert('File size must be less than 10MB');
        e.target.value = '';
        return;
      }
      
      if (!allowedTypes.includes(file.type)) {
        alert('Only images (JPG, PNG, GIF), PDFs, and Word documents are allowed');
        e.target.value = '';
        return;
      }
      
      setReplyFile(file);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !ticket) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error: {error || 'Ticket not found'}</p>
          <div className="mt-4 space-x-2">
            <button
              onClick={() => navigate('/admin/tickets')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Back to Tickets
            </button>
            <button
              onClick={fetchTicketDetail}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto space-y-6" dir="ltr">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/admin/tickets')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="mr-2">←</span>
              Back to Tickets
            </button>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Status:</span>
              {getStatusBadge(ticket.status)}
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Ticket #{ticket.id}
              </h1>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Client:</span>
                  <span>{ticket.client_name} ({ticket.client_email})</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Request:</span>
                  <span>#{ticket.request.request_number} - {ticket.request.company_name}</span>
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium mr-2">Package:</span>
                  <span>{ticket.request.package_name} ({ticket.request.country})</span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <select
                  value={ticket.status}
                  onChange={(e) => handleStatusUpdate(e.target.value)}
                  disabled={statusUpdating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                >
                  <option value="waiting_for_client">Waiting for Client</option>
                  <option value="answered">Answered</option>
                  <option value="processing">Processing</option>
                  <option value="closed">Closed</option>
                </select>
                {statusUpdating && (
                  <div className="text-xs text-gray-500">Updating status...</div>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500 space-y-1">
                <div>Created: {formatDateDisplay(ticket.created_at)}</div>
                <div>Updated: {formatDateDisplay(ticket.updated_at)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Conversation</h2>
          
          <div className="space-y-6">
            {ticket.replies.map((reply, index) => (
              <div
                key={reply.id}
                className={`flex ${reply.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-2xl w-full ${
                    reply.sender === 'admin'
                      ? 'bg-primary-50 border-l-4 border-primary-500'
                      : 'bg-gray-50 border-l-4 border-gray-300'
                  } rounded-lg p-4`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${
                      reply.sender === 'admin' ? 'text-primary-700' : 'text-gray-700'
                    }`}>
                      {reply.sender === 'admin' ? 'Support Team' : ticket.client_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDateDisplay(reply.created_at)}
                    </span>
                  </div>
                  
                  <p className="text-gray-800 whitespace-pre-wrap mb-3">
                    {reply.message}
                  </p>
                  
                  {reply.attachment && (
                    <div className="border-t border-gray-200 pt-3">
                      <a
                        href={`https://patrixglobal.com/${reply.attachment.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-primary-600 hover:text-primary-800"
                      >
                        <span className="mr-2">📎</span>
                        {reply.attachment.filename}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reply Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Reply to Ticket</h3>
          
          <form onSubmit={handleReplySubmit} className="space-y-4">
            <div>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Write your reply..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-vertical"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attach File (Optional)
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              {replyFile && (
                <div className="mt-2 text-sm text-gray-600">
                  Selected: {replyFile.name}
                  <button
                    type="button"
                    onClick={() => setReplyFile(null)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Maximum file size: 10MB. Allowed: Images, PDFs, Word documents.
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Reply as: <span className="font-medium text-primary-700">Support Team</span>
              </div>
              <button
                type="submit"
                disabled={submitting || !replyMessage.trim()}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTicketDetail;